import type { StoryStatus } from 'we-tree-api-client/v2';
import { canModifyItem, type ItemAccessFields } from '@/types/access';

/** Matches we-tree-api storiesService validation. */
export const STORY_TITLE_MAX_LENGTH = 255;

/** Max memory body length — short-form stories; keep in sync with API. */
export const STORY_CONTENT_MAX_LENGTH = 5000;

/** Payload for creating a story — store assigns client id before API call. */
export type CreateStoryPayload = {
  familyMemberId: string;
  title: string;
  content: string;
  /** Media asset ids to link after the story is created. */
  mediaAssetIds?: string[];
};

/** Payload for updating a story. */
export type UpdateStoryPayload = {
  title: string;
  content: string;
  /** Full desired set of linked media asset ids (store diff-links/unlinks). */
  mediaAssetIds?: string[];
};

export function isStoryPending(status: StoryStatus | string): boolean {
  return status === 'pending';
}

export function canCreateStoriesForMember(canModifyMember: boolean, effectiveRole: string | null): boolean {
  if (effectiveRole === 'viewer') return false;
  if (canModifyMember) {
    return true;
  }
  return effectiveRole === 'contributor';
}

export function canEditOrDeleteStories(canModifyMember: boolean, effectiveRole: string | null): boolean {
  if (!canModifyMember) return false;
  if (!effectiveRole || effectiveRole === 'viewer' || effectiveRole === 'contributor') return false;
  return true;
}

/** The access fields the API resolves per memory. */
export type StoryAccessFields = ItemAccessFields;

/**
 * Whether the signed-in user may edit or delete one memory.
 *
 * The API resolves access per memory, so a permission granted on the memory itself, or on the
 * member it is about, is only visible here — a family role alone cannot see it. Older API builds
 * omit these fields, hence the caller-supplied fallback.
 */
export function canEditOrDeleteStory(story: StoryAccessFields | null | undefined, fallback: boolean): boolean {
  return canModifyItem(story, fallback);
}

export function storyWordCount(content: string): number {
  const trimmed = content.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function storyReadMinutes(content: string): number {
  const words = storyWordCount(content);
  return Math.max(1, Math.ceil(words / 200));
}

export function formatStoryDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return iso;
  }
}

/**
 * Human-readable attachment summary for memory cards.
 * Uses MIME categories when attachment rows are present; otherwise falls back to count.
 */
export function formatMemoryAttachmentLabel(
  attachmentCount: number,
  attachments: ReadonlyArray<{ mimeType?: string | null }> = []
): string {
  const count = Math.max(attachmentCount, attachments.length);
  if (count <= 0) return '';

  if (attachments.length === 0) {
    return count === 1 ? '1 attachment' : `${count} attachments`;
  }

  let photos = 0;
  let videos = 0;
  let audio = 0;
  let other = 0;
  for (const attachment of attachments) {
    const mime = (attachment.mimeType || '').toLowerCase();
    if (mime.startsWith('image/')) photos += 1;
    else if (mime.startsWith('video/')) videos += 1;
    else if (mime.startsWith('audio/')) audio += 1;
    else other += 1;
  }

  const remaining = Math.max(0, count - attachments.length);
  const parts: string[] = [];
  if (photos > 0) parts.push(photos === 1 ? '1 photo' : `${photos} photos`);
  if (videos > 0) parts.push(videos === 1 ? '1 video' : `${videos} videos`);
  if (audio > 0) parts.push(audio === 1 ? '1 audio file' : `${audio} audio files`);
  if (other > 0) parts.push(other === 1 ? '1 file' : `${other} files`);
  if (remaining > 0) parts.push(remaining === 1 ? '1 more' : `${remaining} more`);

  return parts.length > 0 ? parts.join(', ') : count === 1 ? '1 attachment' : `${count} attachments`;
}
